import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Radar, Search, ExternalLink, Plus, ShieldQuestion, Loader2, Building2 } from "lucide-react";
import { fmtDate } from "@/lib/constants";

const FIT_STYLES = {
  "High Fit": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Medium Fit": "bg-amber-100 text-amber-800 border-amber-200",
  "Low Fit": "bg-slate-100 text-slate-600 border-slate-200",
};

export default function OpportunityTriggerFinder() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    keywords: "CMMC", subtier: "", naics: "", posted_from: "", posted_to: "",
    notice_type: "", set_aside: "", place_of_performance: "", active_only: true, min_fit: 0,
  });
  const [state, setState] = useState({ status: "idle", results: [] });
  const [selected, setSelected] = useState(null);
  const [guidance, setGuidance] = useState(null);
  const [adding, setAdding] = useState(false);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const search = async () => {
    setState({ status: "loading", results: [] });
    try {
      const res = await api.post("/sam/search", filters);
      setState(res.data.status === "ok" ? { ...res.data } : res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Search failed";
      setState({ status: "error", message: msg, results: [] });
      toast.error(msg);
    }
  };

  const openDetail = async (opp) => {
    setSelected(opp);
    setGuidance(null);
    try {
      const res = await api.post("/sam/guidance", { opportunity: opp });
      setGuidance(res.data);
    } catch { /* non-blocking */ }
  };

  const addToLeads = async (opp) => {
    setAdding(true);
    try {
      const res = await api.post("/sam/add-to-leads", { opportunity: opp });
      toast.success("Opportunity added to Leads as a sourced lead");
      setSelected(null);
      navigate(`/leads/${res.data.id}`);
    } catch {
      toast.error("Failed to add lead");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div>
      <div className="px-6 md:px-8 pt-2 pb-4">
        <p className="text-sm text-muted-foreground max-w-3xl">
          Secondary workflow. Search <span className="font-medium text-navy">open opportunities</span> whose language
          references CMMC/CUI/NIST/SPRS. Use this as a trigger signal — the primary workflow is finding awarded DoD contractors.
        </p>
      </div>

      <div className="px-6 md:px-8 pb-8 space-y-6">
        {/* Filters */}
        <div className="rounded-md border border-border bg-card p-5">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Field label="Keywords" testid="sam-keywords" value={filters.keywords} onChange={(v) => set("keywords", v)} placeholder="CMMC, CUI, NIST 800-171…" />
            <Field label="Agency / Subtier" value={filters.subtier} onChange={(v) => set("subtier", v)} placeholder="Department of the Army" />
            <Field label="NAICS" value={filters.naics} onChange={(v) => set("naics", v)} placeholder="541512" />
            <Field label="Notice Type (ptype)" value={filters.notice_type} onChange={(v) => set("notice_type", v)} placeholder="o = solicitation" />
            <Field label="Posted From" value={filters.posted_from} onChange={(v) => set("posted_from", v)} placeholder="MM/DD/YYYY" />
            <Field label="Posted To" value={filters.posted_to} onChange={(v) => set("posted_to", v)} placeholder="MM/DD/YYYY" />
            <Field label="Set-Aside" value={filters.set_aside} onChange={(v) => set("set_aside", v)} placeholder="SB, 8A…" />
            <Field label="Place of Performance (state)" value={filters.place_of_performance} onChange={(v) => set("place_of_performance", v)} placeholder="VA" />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch id="active-only" data-testid="sam-active-only" checked={filters.active_only} onCheckedChange={(v) => set("active_only", v)} />
                <Label htmlFor="active-only" className="text-xs cursor-pointer">Active only</Label>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs">Min fit score</Label>
                <Input type="number" className="w-20 h-9" value={filters.min_fit} onChange={(e) => set("min_fit", parseInt(e.target.value) || 0)} />
              </div>
            </div>
            <Button onClick={search} data-testid="sam-search-button" className="bg-gold text-navy hover:bg-gold-dark font-semibold">
              <Search className="h-4 w-4 mr-2" /> Search SAM.gov
            </Button>
          </div>
        </div>

        {/* Results */}
        {state.status === "loading" && (
          <div className="flex flex-col items-center py-16 gap-3 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin text-gold-dark" /> Searching SAM.gov…
          </div>
        )}
        {state.status === "not_configured" && (
          <div className="rounded-md border-l-4 border-l-gold bg-slate-50 border border-border p-5" data-testid="sam-not-configured">
            <div className="flex items-center gap-2 text-navy font-medium mb-2"><ShieldQuestion className="h-4 w-4 text-gold-dark" /> Not connected</div>
            <p className="text-sm text-muted-foreground">{state.message}</p>
          </div>
        )}
        {state.status === "error" && <p className="text-red-600 text-sm">{state.message}</p>}

        {state.status === "ok" && (
          <div className="rounded-md border border-border bg-card overflow-hidden">
            <div className="px-4 py-3 border-b border-border text-xs text-muted-foreground">{state.count} opportunities · ranked by fit</div>
            <div className="overflow-x-auto table-scroll">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-border text-left">
                    {["Title", "Solicitation", "Agency", "Posted", "Deadline", "NAICS", "Set-Aside", "Type", "Keyword Hits", "Fit", ""].map((h) => (
                      <th key={h} className="px-4 py-3 font-semibold text-navy text-xs uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {state.results.length === 0 ? (
                    <tr><td colSpan={11} className="px-4 py-10 text-center text-muted-foreground">No opportunities matched.</td></tr>
                  ) : state.results.map((o, i) => (
                    <tr key={i} data-testid={`sam-result-${i}`} onClick={() => openDetail(o)}
                      className="border-b border-border last:border-0 hover:bg-slate-50 cursor-pointer">
                      <td className="px-4 py-3 font-medium text-navy max-w-[260px]"><span className="line-clamp-2">{o.title}</span></td>
                      <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{o.solicitation_number || "—"}</td>
                      <td className="px-4 py-3 text-xs max-w-[180px] truncate" title={o.agency}>{o.agency || "—"}</td>
                      <td className="px-4 py-3 text-xs whitespace-nowrap text-muted-foreground">{fmtDate(o.posted_date)}</td>
                      <td className="px-4 py-3 text-xs whitespace-nowrap text-muted-foreground">{fmtDate(o.response_deadline)}</td>
                      <td className="px-4 py-3 font-mono text-xs">{o.naics || "—"}</td>
                      <td className="px-4 py-3 text-xs">{o.set_aside || "—"}</td>
                      <td className="px-4 py-3 text-xs">{o.notice_type || "—"}</td>
                      <td className="px-4 py-3 text-xs max-w-[160px]"><span className="line-clamp-1">{(o.keyword_hits || []).join(", ") || "—"}</span></td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-[11px] px-2 py-0.5 rounded border ${FIT_STYLES[o.fit_label]}`}>{o.fit_label} · {o.fit_score}</span>
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="outline" data-testid={`sam-add-${i}`} onClick={() => addToLeads(o)} className="whitespace-nowrap">
                          <Plus className="h-3.5 w-3.5 mr-1" /> Add
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sam-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-lg text-navy pr-8">{selected.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2 text-sm">
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
                  <Info label="Solicitation #" value={selected.solicitation_number} mono />
                  <Info label="Agency" value={selected.agency} />
                  <Info label="Subtier" value={selected.subtier} />
                  <Info label="NAICS" value={selected.naics} mono />
                  <Info label="Posted" value={fmtDate(selected.posted_date)} />
                  <Info label="Response deadline" value={fmtDate(selected.response_deadline)} />
                  <Info label="Set-aside" value={selected.set_aside} />
                  <Info label="Notice type" value={selected.notice_type} />
                </div>
                <span className={`inline-block text-xs px-2 py-0.5 rounded border ${FIT_STYLES[selected.fit_label]}`}>{selected.fit_label} · {selected.fit_score} fit</span>

                {selected.description && (
                  <div><p className="text-xs text-muted-foreground mb-1">Description excerpt</p><p className="text-slate-700 leading-relaxed">{selected.description}</p></div>
                )}
                {(selected.keyword_hits || []).length > 0 && (
                  <div><p className="text-xs text-muted-foreground mb-1">Keyword hits (evidence)</p><p className="text-navy">{selected.keyword_hits.join(", ")}</p></div>
                )}

                {!guidance ? (
                  <p className="text-xs text-muted-foreground">Generating guidance…</p>
                ) : (
                  <div className="space-y-3 rounded-md border-l-4 border-l-gold bg-slate-50 border border-border p-4">
                    <Guide title="Why this may matter" text={guidance.why_it_matters} />
                    <Guide title="CMMC/CUI relevance" text={guidance.cmmc_relevance} />
                    <Guide title="Suggested sales angle" text={guidance.outreach_angle} />
                    <Guide title="Suggested email" text={guidance.suggested_email} pre />
                    <Guide title="Suggested phone opener" text={guidance.suggested_phone} />
                    <Guide title="Research next step" text={guidance.research_next_step} />
                  </div>
                )}

                {selected.ui_link && (
                  <a href={selected.ui_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-gold-dark text-sm hover:underline">
                    <ExternalLink className="h-3.5 w-3.5" /> View on SAM.gov
                  </a>
                )}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setSelected(null)}>Close</Button>
                <Button onClick={() => addToLeads(selected)} disabled={adding} data-testid="sam-detail-add" className="bg-navy text-white hover:bg-navy-700">
                  <Plus className="h-4 w-4 mr-2" /> {adding ? "Adding…" : "Add to Leads"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid, raw }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input data-testid={testid} type={type} value={value} placeholder={placeholder}
        onChange={(e) => onChange(raw ? e.target.value : e.target.value)} />
    </div>
  );
}
function Info({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-3 py-1 border-b border-border/60">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className={`text-navy text-right ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</span>
    </div>
  );
}
function Guide({ title, text, pre }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-gold-dark font-semibold mb-1">{title}</p>
      {pre ? <pre className="text-sm whitespace-pre-wrap font-sans text-slate-700 leading-relaxed">{text}</pre>
        : <p className="text-sm text-slate-700 leading-relaxed">{text}</p>}
    </div>
  );
}
